// Notion Integration Service for Way2Will
// Handles OAuth flow and page/database fetching from Notion

// OAuth Configuration - Set these in your .env file
const NOTION_CLIENT_ID = import.meta.env.VITE_NOTION_CLIENT_ID || '';
const NOTION_CLIENT_SECRET = import.meta.env.VITE_NOTION_CLIENT_SECRET || '';
const NOTION_REDIRECT_URI = import.meta.env.VITE_NOTION_REDIRECT_URI || 'http://localhost:5173/app/connections/notion/callback';

// Token structure
export interface NotionTokens {
    access_token: string;
    bot_id: string;
    workspace_id: string;
    workspace_name: string;
    workspace_icon?: string;
}

// Notion page structure
export interface NotionPage {
    id: string;
    title: string;
    icon?: string;
    url: string;
    last_edited_time: string;
    created_time: string;
    parent_type: 'database_id' | 'page_id' | 'workspace';
}

// Storage keys
const STORAGE_KEY_TOKENS = 'way2will_notion_tokens';

/**
 * Check if Notion OAuth is properly configured
 */
export function isNotionConfigured(): boolean {
    return !!NOTION_CLIENT_ID && !!NOTION_CLIENT_SECRET;
}

/**
 * Initiate Notion OAuth flow
 */
export function initiateNotionOAuth(): void {
    if (!NOTION_CLIENT_ID) {
        console.error('Notion Client ID not configured. Set VITE_NOTION_CLIENT_ID in .env');
        alert('Notion integration not configured. Please set up credentials in .env file.');
        return;
    }

    // Generate state for CSRF protection
    const state = JSON.stringify({
        service: 'notion',
        timestamp: Date.now(),
        nonce: Math.random().toString(36).substring(7),
    });

    // Store state for verification on callback
    localStorage.setItem('notion_oauth_state', state);

    // Build OAuth URL
    const params = new URLSearchParams({
        client_id: NOTION_CLIENT_ID,
        redirect_uri: NOTION_REDIRECT_URI,
        response_type: 'code',
        owner: 'user',
        state: state,
    });

    const authUrl = `https://api.notion.com/v1/oauth/authorize?${params.toString()}`;

    // Redirect to Notion OAuth
    window.location.href = authUrl;
}

/**
 * Exchange authorization code for tokens
 */
export async function exchangeNotionCodeForTokens(code: string): Promise<NotionTokens | null> {
    if (!NOTION_CLIENT_ID || !NOTION_CLIENT_SECRET) {
        console.error('Notion credentials not configured');
        return null;
    }

    try {
        // Create basic auth header
        const basicAuth = btoa(`${NOTION_CLIENT_ID}:${NOTION_CLIENT_SECRET}`);

        const response = await fetch('https://api.notion.com/v1/oauth/token', {
            method: 'POST',
            headers: {
                'Authorization': `Basic ${basicAuth}`,
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                grant_type: 'authorization_code',
                code,
                redirect_uri: NOTION_REDIRECT_URI,
            }),
        });

        const data = await response.json();

        if (data.error) {
            console.error('Notion token exchange failed:', data.error);
            return null;
        }

        const tokens: NotionTokens = {
            access_token: data.access_token,
            bot_id: data.bot_id,
            workspace_id: data.workspace_id,
            workspace_name: data.workspace_name || 'Workspace',
            workspace_icon: data.workspace_icon,
        };

        // Store tokens
        localStorage.setItem(STORAGE_KEY_TOKENS, JSON.stringify(tokens));

        return tokens;
    } catch (error) {
        console.error('Error exchanging Notion code for tokens:', error);
        return null;
    }
}

/**
 * Get stored tokens
 */
export function getNotionTokens(): NotionTokens | null {
    const stored = localStorage.getItem(STORAGE_KEY_TOKENS);
    if (!stored) return null;

    try {
        return JSON.parse(stored);
    } catch {
        return null;
    }
}

/**
 * Check if Notion is connected
 */
export function isNotionConnected(): boolean {
    return !!getNotionTokens();
}

/**
 * Revoke Notion access and clear stored data
 */
export async function revokeNotionAccess(): Promise<void> {
    // Notion doesn't have a revoke endpoint, just clear local storage
    localStorage.removeItem(STORAGE_KEY_TOKENS);
}

/**
 * Fetch recent pages from Notion
 */
export async function fetchNotionPages(): Promise<NotionPage[]> {
    const tokens = getNotionTokens();
    if (!tokens?.access_token) {
        console.error('No Notion tokens available');
        return [];
    }

    try {
        const response = await fetch('https://api.notion.com/v1/search', {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${tokens.access_token}`,
                'Content-Type': 'application/json',
                'Notion-Version': '2022-06-28',
            },
            body: JSON.stringify({
                sort: {
                    direction: 'descending',
                    timestamp: 'last_edited_time',
                },
                page_size: 20,
            }),
        });

        const data = await response.json();

        if (data.results) {
            return data.results.map((page: any) => ({
                id: page.id,
                title: getPageTitle(page),
                icon: getPageIcon(page),
                url: page.url,
                last_edited_time: page.last_edited_time,
                created_time: page.created_time,
                parent_type: page.parent?.type || 'workspace',
            }));
        }

        return [];
    } catch (error) {
        console.error('Error fetching Notion pages:', error);
        return [];
    }
}

/**
 * Extract page title from Notion page object
 */
function getPageTitle(page: any): string {
    if (page.properties?.title?.title?.[0]?.plain_text) {
        return page.properties.title.title[0].plain_text;
    }
    if (page.properties?.Name?.title?.[0]?.plain_text) {
        return page.properties.Name.title[0].plain_text;
    }
    return 'Untitled';
}

/**
 * Extract page icon from Notion page object
 */
function getPageIcon(page: any): string | undefined {
    if (page.icon?.emoji) {
        return page.icon.emoji;
    }
    if (page.icon?.external?.url) {
        return page.icon.external.url;
    }
    return undefined;
}

/**
 * Convert Notion pages to inbox items format
 */
export function notionPagesToInboxItems(pages: NotionPage[]): Array<{
    id: string;
    title: string;
    preview: string;
    source: 'notion';
    sourceIcon: string;
    timestamp: Date;
    category: 'actionable' | 'fyi' | 'bundled';
    isRead: boolean;
    notionData: NotionPage;
}> {
    return pages.map((page) => ({
        id: `notion-${page.id}`,
        title: page.title,
        preview: `Updated ${new Date(page.last_edited_time).toLocaleDateString()}`,
        source: 'notion' as const,
        sourceIcon: page.icon || '📝',
        timestamp: new Date(page.last_edited_time),
        category: 'fyi' as const,
        isRead: false,
        notionData: page,
    }));
}
